
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DollarSign, ExternalLink, Save, RefreshCw } from 'lucide-react';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';

const AdminSiteConfigPricingForm = () => {
  const { toast } = useToast();
  const { updateConfiguration } = useSiteConfig();
  const { value: currentPricingUrl, loading, refetch } = useSiteConfigValue('google_form_pricing');
  const [saving, setSaving] = useState(false);
  const [tempUrl, setTempUrl] = useState('');

  React.useEffect(() => {
    if (currentPricingUrl && !tempUrl) {
      setTempUrl(currentPricingUrl);
      console.log('AdminSiteConfigPricingForm - Set tempUrl to:', currentPricingUrl);
    }
  }, [currentPricingUrl, tempUrl]);

  const handleUpdate = async () => {
    if (!tempUrl.trim()) {
      toast({
        title: "Validation Error",
        description: "URL cannot be empty.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      console.log('AdminSiteConfigPricingForm - Updating pricing URL from:', currentPricingUrl, 'to:', tempUrl);
      
      const result = await updateConfiguration('google_form_pricing', tempUrl.trim());
      console.log('AdminSiteConfigPricingForm - Update result:', result);
      
      setTimeout(() => {
        console.log('AdminSiteConfigPricingForm - Forcing refetch...');
        refetch();
      }, 1000);
      
      toast({
        title: "Pricing Form URL Updated",
        description: "The pricing inquiry form URL has been updated successfully.",
      });
    } catch (error) {
      console.error('AdminSiteConfigPricingForm - Error updating pricing URL:', error);
      toast({
        title: "Error",
        description: "Failed to update pricing form URL. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const testUrl = () => {
    if (tempUrl) {
      console.log('AdminSiteConfigPricingForm - Testing URL:', tempUrl);
      window.open(tempUrl, '_blank');
    }
  };

  const handleRefresh = () => {
    console.log('AdminSiteConfigPricingForm - Manual refresh triggered');
    refetch();
  };

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <DollarSign className="w-5 h-5" />
            <span>Pricing Inquiry Form Configuration</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-white/10 rounded w-1/3"></div>
            <div className="h-10 bg-white/10 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <DollarSign className="w-5 h-5" />
          <span>Pricing Inquiry Form Configuration</span>
          <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/30">
            Pricing Section Integration
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-white">Pricing Inquiry Form URL</Label>
          <div className="flex space-x-2">
            <Input
              type="url"
              value={tempUrl}
              onChange={(e) => {
                console.log('AdminSiteConfigPricingForm - URL changed to:', e.target.value);
                setTempUrl(e.target.value);
              }}
              disabled={saving}
              placeholder="https://forms.google.com/..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            {tempUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={testUrl}
                className="text-blue-400 hover:text-blue-300"
                disabled={saving}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              className="text-yellow-400 hover:text-yellow-300"
              disabled={saving}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400">
            This URL will be used for pricing inquiry requests from the website.
          </p>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-400">
            Current: {currentPricingUrl || 'Not set'}
          </div>
          <Button
            onClick={handleUpdate}
            disabled={saving || !tempUrl.trim() || tempUrl === currentPricingUrl}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Updating...' : 'Update URL'}
          </Button>
        </div>

        <div className="text-xs text-gray-500 mt-4 p-2 bg-black/20 rounded">
          <div>Current URL: {currentPricingUrl}</div>
          <div>Temp URL: {tempUrl}</div>
          <div>Loading: {loading.toString()}</div>
          <div>Saving: {saving.toString()}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigPricingForm;
